/**
 * The fixed half of the colour system: the neutral ramp, the feedback hues, and the
 * default brand colour used until a tenant supplies its own.
 *
 * Two properties of this palette are design decisions rather than taste, and both come
 * straight out of the brief:
 *
 * 1. **The neutrals are nearly achromatic**, at a hue that only just leans cool. They have
 *    to sit underneath a brand colour nobody has chosen yet — a warm, tinted neutral
 *    would fight half the hues a tenant could pick. The identity of the system lives in
 *    its structure, type and shape language, not in a hue we do not own.
 * 2. **The feedback hues are system-owned and identical in every tenant.** A tenant can
 *    rebrand its buttons; it cannot rebrand "expired". "Done" has to mean the same green
 *    in every tenant and on every surface, or the status badges stop being readable at a
 *    glance, which is the one thing `docs/reference/FUNCTIONALITY.md` asks of them.
 *
 * The light and dark values are two designs, not one inverted. In light, depth is a
 * shadow over a paper-toned canvas; in dark, shadows are close to invisible, so depth is
 * carried by the surface's own lightness and the feedback hues are pulled up in lightness
 * and down in chroma so they do not glow.
 */

/**
 * The neutral ramp. Indexed the way a designer reads it: 0 is white, 1000 is near-black.
 *
 * Re-pointed at the 2026-10 redesign's neutrals (`docs/ux/design/2026-10/source/t4s.css`):
 * `050` is the light canvas, `100` the sunken surface, `200` the hairline border and `300`
 * the strong border. The rest of the ramp is unchanged.
 */
export const neutral = {
  '000': '#ffffff',
  '025': '#fafbfc',
  '050': '#f4f6f8',
  '100': '#eef1f4',
  '200': '#e1e6eb',
  '300': '#c8d0d9',
  '400': '#98a1ae',
  '500': '#7a838f',
  '600': '#525a66',
  '700': '#3b4450',
  '800': '#29303a',
  '900': '#1b2129',
  '950': '#12171d',
  '1000': '#0a0d11',
} as const

/**
 * The default brand colour, used when a tenant has supplied nothing and on the login
 * screen before the config endpoint has answered.
 *
 * A deep ink violet: far enough from the feedback hues that a brand-coloured control is
 * never mistaken for a status, and dark enough to carry white text at more than 8:1 in
 * light mode without any correction at all.
 */
export const DEFAULT_BRAND_COLOR = '#4c3fbf'

/** The two colours a brand fill is allowed to put text in. Nothing else goes on a brand. */
export const ON_BRAND_CANDIDATES = ['#ffffff', neutral['950']] as const

export type ToneName = 'neutral' | 'info' | 'success' | 'warning' | 'danger'

export type TonePalette = {
  /** A filled block of this tone: the badge with strong emphasis, the status edge. */
  readonly solid: string
  /** Text and icons drawn on `solid`. */
  readonly onSolid: string
  /** Text and icons in this tone, drawn on the theme's own surfaces or on `surface`. */
  readonly text: string
  /** A tinted background: the quiet badge, the callout, the toast body. */
  readonly surface: string
  /** The border around `surface`. */
  readonly border: string
}

export type TonePalettes = Readonly<Record<ToneName, TonePalette>>

/**
 * Light-mode feedback hues — the 2026-10 redesign's five tones, verbatim
 * (`t4s.css`: `--n-*`, `--i-*`, `--s-*`, `--w-*`, `--d-*`).
 *
 * Fixed in every tenant: a red badge means the same in every company. `solid` is dark
 * enough to carry white text and to stand against both the canvas and a card.
 */
export const LIGHT_TONES: TonePalettes = {
  neutral: {
    solid: '#58626e',
    onSolid: '#ffffff',
    text: '#3b4450',
    surface: '#eef1f4',
    border: '#c8d0d9',
  },
  info: {
    solid: '#2459bd',
    onSolid: '#ffffff',
    text: '#1b4f9c',
    surface: '#e8f1fd',
    border: '#b4cdf2',
  },
  success: {
    solid: '#127355',
    onSolid: '#ffffff',
    text: '#0d5c43',
    surface: '#e4f3ec',
    border: '#a6d6c0',
  },
  warning: {
    solid: '#a86400',
    onSolid: '#ffffff',
    text: '#784900',
    surface: '#fcf2df',
    border: '#eccb8c',
  },
  danger: {
    solid: '#bc2f28',
    onSolid: '#ffffff',
    text: '#a1251e',
    surface: '#fcebe9',
    border: '#f0b6b0',
  },
}

/**
 * Dark-mode feedback hues — the redesign's dark tones, verbatim.
 *
 * Not the light values inverted: lighter and less saturated, because a saturated mid-tone
 * on a near-black canvas both vibrates and fails contrast. `onSolid` flips to near-black,
 * since these fills are now light (the design's `#0b0e12` on a solid badge).
 */
export const DARK_TONES: TonePalettes = {
  neutral: {
    solid: '#8793a0',
    onSolid: '#0b0e12',
    text: '#c4ccd5',
    surface: '#232a33',
    border: '#3a4450',
  },
  info: {
    solid: '#5b93e6',
    onSolid: '#0b0e12',
    text: '#9cc2f7',
    surface: '#14243b',
    border: '#2c4b78',
  },
  success: {
    solid: '#3fa57f',
    onSolid: '#0b0e12',
    text: '#86d3b2',
    surface: '#11291f',
    border: '#245640',
  },
  warning: {
    solid: '#d99a2b',
    onSolid: '#0b0e12',
    text: '#f0c47a',
    surface: '#2c2210',
    border: '#5e4718',
  },
  danger: {
    solid: '#e0605a',
    onSolid: '#0b0e12',
    text: '#f4a49d',
    surface: '#33171a',
    border: '#6a2d2a',
  },
}
